import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  fetchPublishedArticles,
  ARTICLE_CATEGORY_LABELS,
  type CommunityArticle,
} from "@/lib/articles.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/community/articles/")({
  head: () => ({
    meta: [
      { title: "News & Articles | UK Trading Card Guides | BidNova Community" },
      {
        name: "description",
        content:
          "Trading card news, market insights, buying guides and community stories from BidNova Auctions.",
      },
      { property: "og:title", content: "News & Articles — BidNova Community" },
      {
        property: "og:description",
        content: "UK trading card news, buying guides and market insights.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/community/articles" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/community/articles" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Community", path: "/community" }, { name: "Articles", path: "/community/articles" }])],
  }),
  component: ArticlesIndex,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function ArticlesIndex() {
  const { t } = useTranslation("community");
  const { data = [], isLoading } = useQuery({
    queryKey: ["community", "articles", "all"],
    queryFn: () => fetchPublishedArticles(60),
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);

  const categories = useMemo(() => {
    const set = new Set<string>();
    data.forEach((a) => set.add(a.category));
    return Array.from(set);
  }, [data]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((a) => {
      if (cat && a.category !== cat) return false;
      if (!query) return true;
      return (
        a.title.toLowerCase().includes(query) ||
        (a.excerpt ?? "").toLowerCase().includes(query) ||
        a.tags.some((t) => t.toLowerCase().includes(query))
      );
    });
  }, [data, q, cat]);

  return (
    <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-10 pb-16">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-display text-3xl text-white">{t("articles.index.title", "News & Articles")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("articles.index.subtitle", "Market insights, buying guides and stories from the UK card community.")}
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("articles.index.searchPlaceholder", "Search articles…")}
            aria-label={t("articles.index.searchLabel", "Search articles")}
            className="h-10 pl-9 bg-background/60 border-border/60"
          />
        </div>
      </div>

      {categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCat(null)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              cat === null
                ? "border-primary/50 bg-primary/15 text-white"
                : "border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"
            }`}
          >
            {t("articles.index.all", "All")}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                cat === c
                  ? "border-primary/50 bg-primary/15 text-white"
                  : "border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"
              }`}
            >
              {ARTICLE_CATEGORY_LABELS[c] ?? c}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[340px] rounded-2xl border border-white/10 skeleton-shimmer" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-muted-foreground">
          {t("articles.index.empty", "No articles match your filters yet.")}
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => <ArticleCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function ArticleCard({ a }: { a: CommunityArticle }) {
  const { t } = useTranslation("community");
  return (
    <Link
      to="/community/articles/$slug"
      params={{ slug: a.slug }}
      className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.4)]"
    >
      {a.cover_image_url && (
        <div className="aspect-[16/9] overflow-hidden bg-black/40">
          <SmartImage
            src={a.cover_image_url}
            alt={`Cover image for ${a.title}`}
            width={480}
            height={270}
            widths={[360, 480, 720, 960]}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 380px"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-5">
        <p className="text-[11px] uppercase tracking-widest text-primary">
          {ARTICLE_CATEGORY_LABELS[a.category] ?? a.category}
        </p>
        <h3 className="mt-2 font-display text-lg text-white line-clamp-2">{a.title}</h3>
        {a.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>}
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>{a.author_name ?? t("articles.index.authorFallback", "BidNova")}</span>
          {a.published_at && (
            <time dateTime={a.published_at}>
              {new Date(a.published_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </time>
          )}
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-2 transition-all">
          {t("articles.index.readMore", "Read more")} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </div>
      </div>
    </Link>
  );
}
